import { api } from '$lib/api';
import { getLocaleFromPath } from '$lib/locale/locale';
import type { SearchResponse } from '$lib/models/search-response';
import { error } from '@sveltejs/kit';
import type { PageLoad } from './$types';
import { AssetType, getAssetFromUrl } from '$lib/utils/asset';
import { get, writable } from 'svelte/store';
import type { Tag } from '$lib/models/tag';
import { browser } from '$app/environment';
import { queryToSearch } from '$lib/utils/query';

const genres = writable<Tag[]>();
const moods = writable<Tag[]>();
const energyLevels = writable<Tag[]>();
const instruments = writable<Tag[]>();
const themes = writable<Tag[]>();
const types = writable<Tag[]>();

const categories = writable<Tag[]>();

export const load = (async ({ fetch, params, url, setHeaders }) => {
  const locale = getLocaleFromPath(url.pathname);
  const pageSize = '50';

  const asset = params.asset;
  const categoryType = params.categoryType;
  const categorySections = params.categories?.split('/').filter((a) => a) || [];
  const category = categorySections[0] || null;
  const subCategory = categorySections.length > 1 ? categorySections[1] : null;

  // Normalize the asset/categoryType combination.
  // The only valid ones are:
  // - royalty-free-music/mood/slug
  // - royalty-free-music/genre/slug
  // - sound-effects/category/slug
  // - sound-effects/category/slug/slug

  if (!categoryType && category) {
    // Not Found, categoryType is required
    error(404, { id: '404', message: 'Not found' });
  }
  if (categoryType === 'category') {
    if (asset != 'sound-effects') {
      // Not Found, categoryType does not match SFX
      error(404, {
        id: '404',
        message: 'Not found',
      });
    }
  }
  if (categoryType === 'mood' || categoryType === 'genre' || categoryType === 'instrument' || categoryType === 'theme') {
    if (asset != 'royalty-free-music') {
      // Not Found, categoryType does not match Tracks
      error(404, {
        id: '404',
        message: 'Not found',
      });
    }
  }
  if (categorySections.length > 2) {
    // Not Found, too many category sections
    error(404, {
      id: '404',
      message: 'Not found',
    });
  }
  if (asset != 'sound-effects' && categorySections.length > 1) {
    // Not Found, subCategory is only valid for SFX
    error(404, {
      id: '404',
      message: 'Not found',
    });
  }

  let parsedType: AssetType | null = getAssetFromUrl(asset);
  if (!parsedType) {
    // Not Found, invalid asset
    error(404, {
      id: '404',
      message: 'Not found',
    });
  }
  const assetType = parsedType as AssetType;

  // Build the search params for the initial search API request.
  const searchParams = new URLSearchParams(url.searchParams);

  // Remove tag from search params.
  // This was renamed to tagId, to prevent interal issues with WordPress.
  searchParams.delete('tag');

  searchParams.set('lang', locale);
  searchParams.set('asset', assetType);
  searchParams.set('pageSize', pageSize);

  if (categoryType && category) {
    searchParams.set('categoryType', categoryType);
    searchParams.set('categorySlug', category);
  }
  if (subCategory) {
    searchParams.set('subCategorySlug', subCategory);
  }
  if (asset == 'royalty-free-music') {
    if (!browser || !get(genres)) {
      searchParams.set('withTrackFilters', 'true');
    }
  }
  if (asset == 'sound-effects') {
    if (!browser || !get(categories)) {
      searchParams.set('withCategories', 'true');
    }
  }

  const stringParams = queryToSearch(searchParams);

  const results = await api<SearchResponse>(fetch, '/v1/search/?' + stringParams);

  if (browser) {
    if (results.genres) {
      genres.set(results.genres);
    }
    if (results.moods) {
      moods.set(results.moods);
    }
    if (results.energyLevels) {
      energyLevels.set(results.energyLevels);
    }
    if (results.instruments) {
      instruments.set(results.instruments);
    }
    if (results.themes) {
      themes.set(results.themes);
    }
    if (results.types) {
      types.set(results.types);
    }
    if (results.categories) {
      categories.set(results.categories);
    }
  }

  if (results.totalItems == 0) {
    setHeaders({
      'x-status-code': '404',
    });
  }

  return {
    asset,
    type: assetType,
    categoryType,
    category,
    subCategory,
    pageSize,
    searchParams,
    results: results,
    genres: assetType === AssetType.Track ? get(genres) || results.genres : undefined,
    moods: assetType === AssetType.Track ? get(moods) || results.moods : undefined,
    energyLevels: assetType === AssetType.Track ? get(energyLevels) || results.energyLevels : undefined,
    instruments: assetType === AssetType.Track ? get(instruments) || results.instruments : undefined,
    themes: assetType === AssetType.Track ? get(themes) || results.themes : undefined,
    types: assetType === AssetType.Track ? get(types) || results.types : undefined,
    categories: assetType === AssetType.SFX ? get(categories) || results.categories : undefined,
    showFooter: false,
  };
}) satisfies PageLoad;
